import { LocalStorage } from '@common/Storage';
import { CONFIG } from '@constants/config';
import { LOCAL_STORAGE_KEYS } from '@constants/storage';

export function parsePlaceDetailsResponse(placeDetails) {
  const {
    address_components: addressComponents,
    geometry: { location }
  } = placeDetails;
  const route = addressComponents.find((ac) => ac.types.includes('route'))?.long_name || '';
  const streetNumber = addressComponents.find((ac) => ac.types.includes('street_number'))?.long_name || '';
  const city = addressComponents.find((ac) => ac.types.includes('locality'))?.long_name || '';
  const subLocality = addressComponents.find((ac) => ac.types.includes('sublocality'))?.long_name || '';
  const administrativeArea2 = addressComponents.find((ac) => ac.types.includes('administrative_area_level_2'))?.long_name || '';
  const province = addressComponents.find((ac) => ac.types.includes('administrative_area_level_1'))?.long_name || '';

  const { DEFAULT_COORDINATES: { lat, lng } = {} } = CONFIG.GOOGLE || {};

  return {
    route,
    streetNumber,
    city: subLocality || city || administrativeArea2,
    province,
    lat: location?.lat() || lat,
    lng: location?.lng() || lng
  };
}

export function checkStreetNumber(newNumber, prevNumber) {
  if (prevNumber === newNumber) return true;

  try {
    if (newNumber.toString().includes('-')) {
      const [lowerString, upperString] = newNumber.toString().split('-');
      const prev = parseInt(prevNumber.toString().replace(/\D/g, ''), 10);
      const lower = parseInt(lowerString.replace(/\D/g, ''), 10);
      const upper = parseInt(upperString.replace(/\D/g, ''), 10);
      if (prev >= lower && prev <= upper) return true;
    }
  } catch (err) {
    console.error('Failed to parse street number', err);
  }

  return false;
}

export function getPartialAddress() {
  try {
    return LocalStorage.get(LOCAL_STORAGE_KEYS.PARTIAL_ADDRESS);
  } catch (err) {
    console.error('Error while parsing partial address', err);
    return null;
  }
}

export function setPartialAddress(address) {
  LocalStorage.set(LOCAL_STORAGE_KEYS.PARTIAL_ADDRESS, address);
}

export function deletePartialAddress() {
  if (LocalStorage.get(LOCAL_STORAGE_KEYS.PARTIAL_ADDRESS)) {
    LocalStorage.remove(LOCAL_STORAGE_KEYS.PARTIAL_ADDRESS);
  }
}

export function getAddressLabel(address, { withCity = true, withProvince = true } = {}) {

  if (!address) return '';

  const {
    streetAddress, street, number, province, city
  } = address;

  // Non-partial address
  if (streetAddress) return joinValues([streetAddress, withCity ? city : null, withProvince ? province : null]);

  // Partial address
  return joinValues([street, number, withCity ? city : null, withProvince ? province : null]);
}

export function getAddressContactLabel(address) {
  if (!address) return '';

  const { contactName, contactPhonenumber } = address;
  return joinValues([contactName, contactPhonenumber], ' - ');
}

function joinValues(args, separator = ', ') {
  return args?.filter((a) => a && a.trim && a.trim() !== '').join(separator);
}

export function validateAddressFormat(values = {}, actions = {}, t) {
  const { city, province } = values;
  const { setFieldError } = actions;

  if (city === '' || province === '') {
    setFieldError('street', t('VALIDATION_ERRORS.AUTOCOMPLETE_NOT_SELECTED'));
    setFieldError('number', '');
    return false;
  }

  setFieldError('street', '');
  setFieldError('number', '');
  return true;
}

export function areLocationsDifferent(loc1, loc2) {
  if (!loc1 || !loc2 || !loc1?.lat || !loc1?.lng || !loc2?.lng || !loc2?.lat) return true;
  if (loc1?.lat !== loc2?.lat || loc1?.lng !== loc2?.lng) return true;
  return false;
}

export function isDefaultLocation(location) {
  if (!location?.lat || !location?.lng) return false;
  const { DEFAULT_COORDINATES: { lat, lng } = {} } = CONFIG.GOOGLE || {};
  if (location.lat !== lat || location.lng !== lng) return false;
  return true;
}

export function getIntervalLabel(interval = {}) {
  const { dateLabel, interval: { since, until } = {} } = interval;
  return `${dateLabel}, ${since} - ${until}`;
}

export function parseGeolocation(geolocation) {
  const { lat, lng } = geolocation || {};

  if (!lat || !lng) return null;

  return {
    lat: parseFloat(lat),
    lng: parseFloat(lng),
  };
}
